<template>
    <div>
        <section class="problems border-0 observer-fade">
            <div class="demo-container full">
                <div class="section-header">
                    <h2 class="section-title">
                        Stop Managing Clients in Spreadsheets
                    </h2>
                    <p class="section-subtitle">
                        Store client information for easy retrieval. Link related projects, tasks, invoices or virtual meetings. Stay updated with information on demand.
                    </p>
                </div>

                <div class="demo-container img one-col bg-none p-0">
                    <div class="demo-header">
                        <div class="traffic-light red" />
                        <div class="traffic-light yellow" />
                        <div class="traffic-light green" />
                        <div class="demo-title text--xs">
                            Dashboard / Clients
                        </div>
                    </div>
                        
                    <div class="tasks-demo">
                        <img src="@/assets/img/clients_screen.svg" alt="clients screen image">
                    </div>
                </div>
            </div>
        </section>

        <section id="demo" class="demo observer-fade bg-none">
            <div class="container">
                <div class="demo-container split two-col bg-none">
                    <div class="demo-content line">
                        <h2>Connect related information</h2> 
                        <p>
                            <strong>Simple, intuitive client dashboard</strong>. Keep all client information in sync. Get a clearer picture of your client's needs and tailor your services accordingly.
                        </p>
                    </div>

                    <div class="img-blur">
                        <img src="@/assets/img/client_connect.svg" alt="client connect image">
                    </div>
                </div>
            </div>
        </section>

        <section id="demo" class="demo observer-fade bg-none">
            <div class="container">
                <div class="demo-container two-col split bg-none">
                    <div class="demo-visual border-0 bg-transparent gap-3 grid p-4 w-100">
                        <div class="demo-visual-count">
                            <div>
                                <span class="flex align-items-center">
                                    <IconSvg
                                        name="filter" fill="#637579" width="24px" height="24px"
                                        class="mr--10"
                                    />
                                    <label class="form-label title mb--0">
                                        Filter by status
                                        <span class="ml--3 zm__count__badge text--xs">3</span>
                                    </label>
                                </span>
                            </div>
                        </div>

                        <div class="flex gap-3 justify-content-center align-items-center">
                            <span class="status__map text--xs tag bold tag--purple badge rounded-pill p-1">
                                <span class="status__circle tag--purple__text" />
                                Potential lead
                            </span>
                            <span class="status__map text--xs tag bold tag--blue badge rounded-pill p-1">
                                <span class="status__circle tag--blue__text" />
                                Onboarding
                            </span>
                            <span class="status__map text--xs tag bold tag--pink badge rounded-pill p-1">
                                <span class="status__circle tag--pink__text" />
                                Declined
                            </span>
                        </div>
                    </div>

                    <div class="demo-content right-line">
                        <h2>Get more out of just storing clients</h2> 
                        <p>
                            <strong>More than a platform to store client information</strong>. Organize and query data to get insights into your clients. Filter client data by date ranges, attributes or custom keywords (coming soon).
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <section class="problems border-0 observer-fade bg-none">
            <div class="container">
                <div class="demo-container split two-col">
                    <div class="demo-content line mb--15">
                        <h2>Migrate your data in a few clicks</h2> 
                        <p>
                            <strong>Looking to switch with an existing client list? </strong>Import existing client lists to automatically create new client records.
                        </p>
                    </div>
                    
                    <div class="demo-visual import">
                        <img src="@/assets/img/client_import.svg" alt="client import image">
                    </div>
                </div>
            </div>
        </section>

        <section class="problems border-0 observer-fade bg-none">
            <div class="container">
                <div class="demo-container md">
                    <div class="demo-content mb--15">
                        <h2>Build stronger client relationships</h2> 
                    </div>
                    
                    <div class="demo-container img one-col bg-none p-0">    
                        <div class="grid gap-3 reports-demo-wrap">
                            <div v-for="item in additionalFeatures" :key="item.name" class="reports-demo">
                                <h2>{{ item.name }}</h2>
                                <p>{{ item.desc }}</p>
                            </div>
                        </div>                  
                    </div>
                </div>
            </div>

            <div class="container mt--40">
                <div class="demo-container split two-col bg-none">
                    <div class="demo-content">
                        <h2>Meeting events</h2> 
                        <p>
                            <strong>Book virtual meetings with existing clients without leaving the app</strong>.Create meeting events with clients created on Ziner. 
                        </p>
                        <ul class="demo-features">
                            <li v-for="item in appointmentFeat" :key="item">
                                <span class="check-icon">✓</span>
                                <p>{{ item }}</p>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <div class="demo-visual">
                            <div class="invoice-preview">
                                <img src="@/assets/img/meeting_details.svg">
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="problems border-0 observer-fade bg-none">
            <div class="container">
                <div class="demo-container full">
                    <div class="demo-content line mb--35">
                        <h2>How it works</h2> 
                        <p>
                            <strong>Get up and running in minutes.</strong> Connect to your favorite calendar or scheduling app to start creating and managing virtual meeting events.
                        </p>
                    </div>
                    
                    <div class="demo-container img one-col bg-none p-0">
                        <div class="workflow-steps">
                            <div class="step-card">
                                <div class="step-number">
                                    1
                                </div>
                                <h3 class="step-title">
                                    Connect calendar
                                </h3>
                                <p class="step-description">
                                    Connect to any of our available calendar integrations.
                                </p>

                                <p class="step-description mt--10">
                                    Automatic synchronization after required permissions are granted.
                                </p>
                            </div>
                
                            <div class="step-card">
                                <div class="step-number">
                                    2
                                </div>
                                <h3 class="step-title">
                                    Create meeting event
                                </h3>
                                <p class="step-description">
                                    Create meeting events, fill in the required information, and submit. 
                                </p>

                                <p class="step-description mt--10">
                                    Events created on the dashboard show up on your actual calendar
                                </p>
                            </div>
                
                            <div class="step-card">
                                <div class="step-number">
                                    3
                                </div>
                                <h3 class="step-title">
                                    Get email notifications
                                </h3>
                                <p class="step-description">
                                    Instant email notifications to all parties on event creation.
                                </p>

                                <p class="step-description mt--10">
                                    We notify you when your clients accept your meeting invite.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import IconSvg from "../ui/icons/Icon-Svg.vue";

export default {
    name: "ProductClientManagement",

    components: { IconSvg },
    
    data() {
        return {
            additionalFeatures: [
                { name: "Schedule check-in reminders", desc: "Stay connected with your clients via email check-ins. Set check-in reminders to follow up, sales, intro, physcial meetings...etc" },
                { name: "Client onboarding", desc: "Generate automated steps or checklists to help onboard new clients efficiently." },
                { name: "Data capture", desc: "Access and store information on potential leads who reach out via contact or survey forms." },
            ],
            observer: null,
            appointmentFeat: ["Smart calendar sync", "Instant scheduling", "Service management"],
        };
    },

    async mounted() {
        this.initPageIntersectionObserver();
    },

    beforeDestroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
    },

    methods: {
        initPageIntersectionObserver() {
            this.observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("in-view");
                    }
                });
            }, {
                threshold: 0.1,
                rootMargin: "0px 0px -50px 0px",
            });

            // Observe all elements with observer-fade class
            document.querySelectorAll(".observer-fade").forEach(el => {                
                this.observer.observe(el);
            });
        },
    },
};
</script>

<style lang="scss" scoped>
    @media (max-width: 768px) {
        .features-grid {
            grid-template-columns: 1fr;
        }

        .container {
            padding: 0 1rem;
        }
    }

    .problems {
        padding: 3rem 0;
    }

    .tasks-demo {
        border-radius: 15px;
        background: white;

         &::after {
            content: "";
            pointer-events: none;
            position: absolute;
            inset: 0;
            z-index: 2;
            background: linear-gradient(to bottom, #ffffff21 50%, #faf9f5 100%);
        }
    }

    .tasks-demo img {
        max-width: 100%;
    }

    .img-blur {
        border-radius: 15px;
        position: relative;
        padding: 20px;
        border: 1px solid #e4a37a2e;
        border: 1px solid rgba(0, 0, 0, 0.1);
        border-right: none;

        &::after {
            background: linear-gradient(to right, #ffffff21 50%, #faf9f5 100%);
        }
    }

    .demo-visual.import {
        height: 300px;
    
        img {
            width: 100%;
            height: 100%;
        }

        @include media('>md_768') {
        //    height: auto;
            background: transparent;
        }
    }

    .demo-visual-count {
        border: 2px dashed #CBD5E1;
        border-radius: 30px;
        padding: 20px;
        display: flex;
        align-items: center;
        justify-content: center;

        label {
            color: #637579;
        }
    }

    .reports-demo-wrap {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 2rem !important;
    }

    .reports-demo {
        color: #1e293b;

        h2 {
            font-size: 1.25rem;
            font-weight: 500;
        }

        p {
            font-size: .975rem;
            margin-top: 0.75rem;
            color: #4A5568;
            line-height: 24px;
            color: #64748b;
        }
    }

    .tasks-demo::after, .demo-visual::after {
        content: "";
        pointer-events: none;
        position: absolute;
        inset: 0;
        z-index: 2;
        background: linear-gradient(to bottom, transparent 50%, #faf9f5 100%);
        background: linear-gradient(to bottom, #ffffff21 50%, #faf9f5 100%);
    }

    .workflow-section {
        padding: 140px 0;
        background: #f8fafc;
        border-top: 1px solid #f0f0f0;
        border-bottom: 1px solid #f0f0f0;
    }

    .workflow-steps {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        grid-template-columns: 1fr 1fr 1fr;
        gap: 30px;
        margin-top: 30px;
    }

    .step-card {
        background: #ffffff;
        padding: 20px;
        border-radius: 16px;
        text-align: center;
        border: 1px solid #e5e7eb;
        position: relative;
        box-shadow: 0 4px 25px rgba(0, 0, 0, 0.04);
        width: 300px;
    }

    .step-number {
        position: absolute;
        top: -20px;
        left: 50%;
        transform: translateX(-50%);
        width: 35px;
        height: 35px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        font-size: 1rem;
        color: #065f46;
        background: #dcfce7;
        border: 1px solid rgba(22, 163, 74, .18);
    }

    .step-title {
        font-size: 1.15rem;
        font-weight: 700;
        margin: 20px 0 16px;
        color: #1a1a1a;
    }

    .step-description {
        color: #666666;
        font-size: 0.875rem;
        line-height: 1.5;
    }
</style>